class PaymentMethodsPopover extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.more = this.querySelector('.payment-methods__more');
    this.trigger = this.querySelector('.payment-methods__trigger');
    this.popup = this.querySelector('.payment-methods__popup');

    if (!this.more || !this.trigger || !this.popup) return;

    this.initialized = true;
    this.hasNativePopover = typeof this.popup.showPopover === 'function';
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onPopoverBeforeToggle = this.onPopoverBeforeToggle.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onViewportResize = this.onViewportResize.bind(this);
    this.onScroll = this.onScroll.bind(this);

    if (this.hasNativePopover) {
      this.popup.addEventListener('beforetoggle', this.onPopoverBeforeToggle);
    } else {
      this.popup.hidden = true;
      this.trigger.addEventListener('click', this.onTriggerClick, true);
      document.addEventListener('click', this.onDocumentClick);
    }
    document.addEventListener('keydown', this.onDocumentKeydown);
    window.addEventListener('resize', this.onViewportResize);
    window.addEventListener('scroll', this.onScroll, true);
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.popup.removeEventListener('beforetoggle', this.onPopoverBeforeToggle);
    this.trigger.removeEventListener('click', this.onTriggerClick, true);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.onViewportResize);
    window.removeEventListener('scroll', this.onScroll, true);
    this.initialized = false;
  }

  get isOpen() {
    if (this.hasNativePopover) return this.popup.matches(':popover-open');
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.isOpen ? this.close() : this.open();
  }

  onPopoverBeforeToggle(event) {
    const isOpen = event.newState === 'open';
    this.trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      this.classList.add('is-enhanced');
      this.positionPopup();
    } else {
      this.resetPopupPosition();
    }
  }

  onDocumentClick(event) {
    if (this.isOpen && !this.more.contains(event.target)) this.close();
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.close();
    this.trigger.focus();
  }

  onViewportResize() {
    if (this.isOpen) this.positionPopup();
  }

  onScroll() {
    if (this.isOpen) this.close();
  }

  open() {
    this.popup.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.positionPopup();
    this.classList.add('is-enhanced');
  }

  close() {
    if (this.hasNativePopover) {
      if (this.isOpen) this.popup.hidePopover();
      return;
    }

    this.popup.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.resetPopupPosition();
  }

  resetPopupPosition() {
    this.classList.remove('is-enhanced');
    this.popup.style.removeProperty('left');
    this.popup.style.removeProperty('top');
  }

  positionPopup() {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    const gutter = 0.8 * rootFontSize;
    const offset = 0.8 * rootFontSize;
    const triggerRect = this.trigger.getBoundingClientRect();
    const popupRect = this.popup.getBoundingClientRect();
    const popupWidth =
      popupRect.width || Math.min(14.4 * rootFontSize, window.innerWidth - gutter * 2);
    const popupHeight = popupRect.height || 4.2 * rootFontSize;
    const isMobile = window.matchMedia('(max-width: 46.8125rem)').matches;
    let left;
    let top;

    if (isMobile) {
      left = triggerRect.right - popupWidth;
      top = triggerRect.bottom + offset;

      if (top + popupHeight > window.innerHeight - gutter) {
        top = triggerRect.top - offset - popupHeight;
      }
    } else {
      left = triggerRect.right + offset;

      if (left + popupWidth > window.innerWidth - gutter) {
        left = triggerRect.left - offset - popupWidth;
      }

      top = triggerRect.top + (triggerRect.height - popupHeight) / 2;
    }

    left = Math.max(gutter, Math.min(left, window.innerWidth - popupWidth - gutter));
    top = Math.max(gutter, Math.min(top, window.innerHeight - popupHeight - gutter));

    this.popup.style.left = `${left / rootFontSize}rem`;
    this.popup.style.top = `${top / rootFontSize}rem`;
  }
}

if (!customElements.get('payment-methods-popover')) {
  customElements.define('payment-methods-popover', PaymentMethodsPopover);
}
